package com.example.readingnotes.domain.repository

import com.example.readingnotes.data.remote.oidc.AuthorizationUrlResult
import com.example.readingnotes.domain.model.AuthState
import com.example.readingnotes.domain.model.TotpSetupResponse
import com.example.readingnotes.domain.model.User
import com.example.readingnotes.util.Result

interface AuthRepository {
    suspend fun login(password: String): Result<AuthState>

    suspend fun logout(): Result<Unit>

    suspend fun getAuthState(): Result<AuthState>

    // New methods for username authentication
    suspend fun register(username: String, password: String): Result<AuthState>

    suspend fun loginWithUsername(username: String, password: String): Result<AuthState>

    suspend fun verifyTotp(code: String): Result<Boolean>

    suspend fun setupTotp(): Result<TotpSetupResponse>

    suspend fun updateUsername(newUsername: String): Result<User>

    suspend fun checkUsernameAvailable(username: String): Result<Boolean>

    // Email authentication methods
    suspend fun sendVerificationCode(email: String): Result<Unit>

    suspend fun verifyEmail(email: String, code: String): Result<AuthState>

    suspend fun loginWithEmail(email: String, code: String): Result<AuthState>

    // ============================================================================
    // OIDC authentication methods
    // ============================================================================

    /**
     * Get OIDC authorization URL for login
     * Returns URL and state for the authorization flow
     */
    suspend fun oidcLogin(): Result<AuthorizationUrlResult>

    /**
     * Handle OIDC callback
     * Called after user completes authorization on the OIDC provider
     * @param code Authorization code from callback
     * @param state State parameter for CSRF protection
     */
    suspend fun oidcCallback(code: String, state: String): Result<AuthState>

    /**
     * Refresh OIDC access token
     */
    suspend fun oidcRefresh(): Result<AuthState>

    /**
     * Logout from OIDC session
     */
    suspend fun oidcLogout(): Result<Unit>

    // ============================================================================
    // Profile management methods
    // ============================================================================

    /**
     * Get user profile
     * Returns profile information including username, displayName, bio
     */
    suspend fun getProfile(): Result<UserProfile>

    /**
     * Update user profile
     * @param username Optional username to update
     * @param displayName Optional display name to update
     */
    suspend fun updateProfile(username: String? = null, displayName: String? = null): Result<UserProfile>
}

/**
 * User profile data class
 */
data class UserProfile(
    val id: String,
    val email: String,
    val username: String?,
    val displayName: String?,
    val bio: String?,
    val profileComplete: Boolean,
    val onboardingCompleted: Boolean,
    val onboardingStep: String,
)
